var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useState, useRef } from 'react';
import Label from 'components/v2/forms/Label';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import LocationSearch from 'components/site/location-search';
import Badge from 'components/v2/Badge';
import CustomerSearch from 'components/site/customer-search';
import CollapsibleItem from 'components/v2/list/CollapsibleItem';
import DeleteButton from 'components/v2/buttons/DeleteButton';
import ChevronRightIcon from 'components/v2/icons/ChevronRightIcon';
import ChevronDownIcon from 'components/v2/icons/ChevronDownIcon';
import ToggleSwitch from 'components/v2/ToggleSwitch';
import { ConfirmActionModal } from 'components/ui/modal';
import { useDeleteShipperWithAssociation } from 'hooks/warehouse/useManageWarehouse';
import { showToast } from 'stores/ToastStore';
import Alert from 'components/ui/Alert';
import Table from 'components/v2/Table';
import { checkExistingWarehouse } from 'utils/WarehouseUserAPIUtils';
import { unescapeSpecialChars } from 'helpers/helper_functions';
function Asterisk() {
    return React.createElement("span", { className: "text-xs font-medium text-gray-500" }, "*");
}
const WarehouseForm = ({ data = {}, saving, onValidSuccess, onValidFail, afterSave, }) => {
    const [formData, setFormData] = useState(Object.assign({ shippers: [], address: '', warehouse_location: '' }, data));
    const isFirstRender = useRef(true);
    const [warehouseId, setWarehouseId] = useState('');
    const [showDeleteShipperModal, setShowDeleteShipperModal] = useState(false);
    const [deleteShipperData, setDeleteShipperData] = useState(null);
    const [updating, setUpdating] = useState(false);
    const [errors, setErrors] = useState([]);
    const [isWarehouseExist, setIsWarehouseExist] = useState(false);
    const { t } = useTranslation();
    const onDeleteShipperSuccess = () => {
        setShowDeleteShipperModal(false);
        showToast(t('DELETE_SHIPPER_SUCCESS'), t('DELETE_SHIPPER_SUCCESS'), 'success');
        afterSave();
    };
    const onDeleteShipperFailed = () => {
        showToast(t('DELETE_SHIPPER_FAILED'), t('DELETE_SHIPPER_FAILED'), 'error');
    };
    const { mutation: deleteShipperMutation } = useDeleteShipperWithAssociation(onDeleteShipperSuccess, onDeleteShipperFailed);
    const getInitialData = (initData) => {
        setFormData(Object.assign({ shippers: [], address: '', warehouse_location: '' }, initData));
        setWarehouseId(initData._id);
    };
    useEffect(() => {
        if (Object.keys(data).length) {
            if (isFirstRender.current) {
                getInitialData(data);
            }
            isFirstRender.current = false;
        }
    }, [data]);
    const validateData = (newData) => {
        let warehouseUserErrors = [];
        const { relations: currentShippers = [], warehouse_location } = newData;
        if (!warehouse_location) {
            setErrors([t('REQUIRED_WAREHOUSE_LOCATION')]);
            return;
        }
        const { shippers: initShippers = [] } = data;
        if (!currentShippers.length) {
            setErrors([t('REQUIRED_SHIPPER_UNDER_WAREHOUSE')]);
            return;
        }
        const relations = currentShippers.reduce((acc, { shipper: shipperId, shipperName = '', warehouse_users = [] }) => {
            if (!warehouse_users.length) {
                warehouseUserErrors.push(t('REQUIRED_USER_UNDER_SHIPPER', { shipper: shipperName }));
                return acc;
            }
            const foundShipper = initShippers.find((initShipper) => initShipper._id === shipperId);
            if (!foundShipper) {
                acc.push({ shipper: shipperId, warehouse_users });
            }
            else {
                const users = warehouse_users.reduce((accWarehouse, user) => {
                    const foundUser = foundShipper.warehouseUsers.find((wuser) => wuser._id === user._id);
                    if (!foundUser) {
                        accWarehouse.push(user);
                    }
                    else if (foundUser.status !== user.status ||
                        foundUser.active !== user.active) {
                        accWarehouse.push(user);
                    }
                    return accWarehouse;
                }, []);
                if (users.length) {
                    acc.push({ shipper: shipperId, warehouse_users: users });
                }
            }
            return acc;
        }, []);
        if (warehouseUserErrors.length) {
            setErrors(warehouseUserErrors);
            return;
        }
        setErrors([]);
        const payload = Object.assign({}, newData);
        return payload;
    };
    const renderErrors = () => {
        if (errors.length) {
            onValidFail();
            return (React.createElement(Alert, { type: "error" }, errors.map((errorMessage) => (React.createElement("p", null, errorMessage)))));
        }
    };
    const saveForm = () => {
        const payload = validateData(getData());
        if (payload) {
            onValidSuccess(payload);
        }
    };
    useEffect(() => {
        if (saving) {
            saveForm();
        }
    }, [saving]);
    const handleLocationSearch = async (location) => {
        const { address = '', _id } = location;
        const result = await checkExistingWarehouse(_id);
        if (result === null || result === void 0 ? void 0 : result.success) {
            const isExist = !!(result === null || result === void 0 ? void 0 : result.exists);
            setIsWarehouseExist(isExist);
            if (address && !isExist) {
                setFormData((prev) => (Object.assign(Object.assign({}, prev), { address, warehouse_location: _id })));
            }
        }
    };
    const renderViewMore = (open) => {
        return (React.createElement("div", { className: "flex-shrink-0 flex-grow-0" },
            React.createElement("div", { className: classNames('flex-grow-0 flex-shrink-0 cursor-pointer') },
                !open && React.createElement(ChevronRightIcon, { className: "w-5 h-5" }),
                open && React.createElement(ChevronDownIcon, { className: "w-5 h-5" }))));
    };
    function renderShipperName(name, shipperData) {
        const handleDeleteShipper = () => {
            if (shipperData === null || shipperData === void 0 ? void 0 : shipperData.isFromApi) {
                setDeleteShipperData(shipperData);
                setShowDeleteShipperModal(true);
            }
            else {
                setFormData((prev) => {
                    const updatedShippers = prev.shippers.filter((shipper) => {
                        return shipper._id !== shipperData._id;
                    });
                    return Object.assign(Object.assign({}, prev), { shippers: updatedShippers });
                });
            }
        };
        return function renderHeading(open) {
            return (React.createElement("div", { className: "w-full flex justify-between items-center" },
                React.createElement("div", { className: "flex items-center space-x-2 relative" },
                    renderViewMore(open),
                    React.createElement("div", { className: "flex items-center space-x-1" },
                        React.createElement("h3", { className: "flex-1" }, unescapeSpecialChars(name)))),
                React.createElement(DeleteButton, { color: "red", className: "mr-3", onClick: () => handleDeleteShipper(shipperData) })));
        };
    }
    const isDuplicate = (id = '', userList = []) => {
        const foundUser = userList.find((user) => user._id === id);
        if (foundUser) {
            showToast('', t('USER_ALREADY_EXIST'), 'warning');
        }
        return !!foundUser;
    };
    const handleShipperSelect = (shipperName, shipperItem) => {
        if (isDuplicate(shipperItem._id, formData.shippers)) {
            return;
        }
        setFormData((prev) => (Object.assign(Object.assign({}, prev), { shipperName: shipperName, shippers: [...prev.shippers, Object.assign(Object.assign({}, shipperItem), { warehouseUsers: [] })] })));
    };
    const renderShipperAssociations = () => {
        var _a;
        const handleWarehouseUserSelect = (shipperId, warehouseItem) => {
            setFormData((prev) => {
                const updatedShippers = prev.shippers.map((shipper) => {
                    if (shipper._id === shipperId) {
                        if (!isDuplicate(warehouseItem._id, shipper.warehouseUsers)) {
                            shipper.warehouseUsers = [
                                ...shipper.warehouseUsers,
                                Object.assign(Object.assign({}, warehouseItem), { status: false, active: true }),
                            ];
                        }
                    }
                    return shipper;
                });
                return Object.assign(Object.assign({}, prev), { shippers: updatedShippers });
            });
        };
        const handleWarehouseUserStatusToggle = (val, shipperId, warehouseUserId) => {
            setFormData((prev) => {
                const shippers = prev.shippers.map((shipper) => {
                    if (shipper._id === shipperId) {
                        const updatedWarehouseUsers = shipper.warehouseUsers.map((user) => {
                            if (user._id === warehouseUserId) {
                                user.status = val;
                            }
                            return user;
                        });
                        return Object.assign(Object.assign({}, shipper), { warehouseUsers: updatedWarehouseUsers });
                    }
                    return shipper;
                });
                return Object.assign(Object.assign({}, prev), { shippers });
            });
        };
        const handleDeleteWarehouseUser = (shipperId, warehouseUserId) => {
            setFormData((prev) => {
                const updatedShippers = prev.shippers.map((shipper) => {
                    if (shipper._id === shipperId) {
                        const updatedWarehouseUsers = shipper.warehouseUsers.reduce((acc, warehouseUser) => {
                            if (warehouseUser === null || warehouseUser === void 0 ? void 0 : warehouseUser.isFromApi) {
                                if (warehouseUser._id === warehouseUserId) {
                                    warehouseUser.active = false;
                                }
                                acc.push(warehouseUser);
                            }
                            else if (warehouseUserId !== warehouseUser._id) {
                                acc.push(warehouseUser);
                            }
                            return acc;
                        }, []);
                        return Object.assign(Object.assign({}, shipper), { warehouseUsers: updatedWarehouseUsers });
                    }
                    return shipper;
                });
                return Object.assign(Object.assign({}, prev), { shippers: updatedShippers });
            });
        };
        if ((_a = formData === null || formData === void 0 ? void 0 : formData.shippers) === null || _a === void 0 ? void 0 : _a.length) {
            return formData.shippers.map((_a) => {
                var { warehouseUsers } = _a, shipper = __rest(_a, ["warehouseUsers"]);
                return (React.createElement(CollapsibleItem, { renderTitle: renderShipperName(shipper.company_name ||
                        `${shipper.first_name} ${shipper.last_name}`, shipper), className: "flex-1 my-4", hideIcon: true, defaultOpen: true },
                    React.createElement("div", { "data-testid": "manage-warehouse-form-warehouse-user", className: "mt-4 w-full" },
                        React.createElement(Table, { headers: [
                                { label: t('WAREHOUSE_USER') },
                                { label: t('STATUS') },
                                { label: '' },
                            ], columns: [
                                {
                                    field: 'name',
                                    render: (value, cell) => cell.active
                                        ? value || `${cell.first_name} ${cell.last_name}`
                                        : null,
                                },
                                {
                                    field: 'status',
                                    render: (value, warehouseUser) => warehouseUser.active ? (React.createElement(ToggleSwitch, { className: "-mt-2", enabled: value, onChange: (val) => handleWarehouseUserStatusToggle(val, shipper._id, warehouseUser._id) })) : null,
                                },
                                {
                                    render: (_, warehouseUser) => warehouseUser.active ? (React.createElement(DeleteButton, { className: "float-right", onClick: () => handleDeleteWarehouseUser(shipper._id, warehouseUser._id) })) : null,
                                },
                            ], data: warehouseUsers, footer: () => (React.createElement("div", { className: "m-2" },
                                React.createElement(CustomerSearch, { clearOnSelect: true, clearOnFocus: true, showEmptyMessage: true, placeholder: t('SEARCH_ADD_WAREHOUSE_USER'), type: "warehouses", onSelect: (_, val) => handleWarehouseUserSelect(shipper._id, val) }))) }))));
            });
        }
    };
    const getData = () => {
        const { warehouse_location, _id: warehouse_id = '', shippers = [], } = formData;
        const relations = shippers.map(({ _id, company_name: name, warehouseUsers = [] }) => {
            const warehouse_users = warehouseUsers.map(({ _id, status, active = true, relation_id = '' }) => ({
                _id,
                active,
                status,
                relation_id,
            }));
            return { shipper: _id, warehouse_users, shipperName: name };
        });
        const payload = { warehouse_location, relations };
        if (data.warehouse_location &&
            formData.warehouse_location !== data.warehouse_location) {
            payload.warehouse_id = warehouse_id;
        }
        return payload;
    };
    const handleDeleteShipperApi = async () => {
        setUpdating(true);
        await deleteShipperMutation.mutateAsync({
            warehouseId,
            shipperId: deleteShipperData._id,
        });
        setUpdating(false);
    };
    return (React.createElement(React.Fragment, null,
        renderErrors(),
        React.createElement(ConfirmActionModal, { title: t('DELETE_SHIPPER'), isOpen: showDeleteShipperModal, onClose: () => setShowDeleteShipperModal(false), onConfirm: handleDeleteShipperApi, confirmActionColor: "red", confirmActionText: t('DELETE'), actionDisabled: updating }, t('DELETE_SHIPPER_MSG')),
        React.createElement("div", { "data-testid": "manage-warehouse-form-location-container", className: "sm:w-80 mb-6" },
            React.createElement(Label, { htmlFor: "warehouse-location-search", className: "mb-2" },
                t('ADDRESS'),
                " ",
                React.createElement(Asterisk, null)),
            React.createElement(LocationSearch, { key: "manage-warehouse-location-search", defaultValue: formData === null || formData === void 0 ? void 0 : formData.address, placeholder: t('SEARCH_LOCATION_ADD'), handleLocationSearch: handleLocationSearch, showEmptyMessage: true, emptyMessage: t('NO_LOCATION_RESULTS') }),
            (formData === null || formData === void 0 ? void 0 : formData.address) && (React.createElement(Badge, { withRemove: true, onRemove: () => {
                    setFormData((prev) => (Object.assign(Object.assign({}, prev), { address: '', warehouse_location: '', locationDeleted: true })));
                }, className: "mt-2" }, formData.address))),
        isWarehouseExist && (React.createElement(Alert, { canClose: true, onClose: () => setIsWarehouseExist(false), type: "error" }, t('WAREHOUSE_ALREADY_EXIST'))),
        !isWarehouseExist && (formData === null || formData === void 0 ? void 0 : formData.address) && (React.createElement(React.Fragment, null,
            React.createElement("hr", { className: "my-6" }),
            React.createElement("div", { "data-testid": "manage-warehouse-form-shipper-container", className: "mt-6 flex justiy-end items-end space-x-2 sm:w-80 w-auto" },
                React.createElement("div", { className: "flex-1" },
                    React.createElement(Label, { className: "mb-1" }, t('SHIPPER')),
                    React.createElement(CustomerSearch, { clear: true, clearOnSelect: true, clearOnFocus: true, showEmptyMessage: true, type: "shippers", placeholder: t('SEARCH_ADD_SHIPPER'), onSelect: (shipperName, shipperItem) => {
                            handleShipperSelect(shipperName, shipperItem);
                        } }))))),
        renderShipperAssociations()));
};
export default WarehouseForm;
